import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import buildWidgetResumeUrl, { decodeResumeToken } from "../util/buildWidgetResumeUrl";

const WIDGET_URL = process.env.REACT_APP_BOOKING_WIDGET_URL;
const API_URL = process.env.REACT_APP_API_URL;

// Bilingual copy. Language comes from the lead (additionalInformation.language,
// "English" | "Spanish" — same values the widget stores). Defaults to English.
const COPY = {
  en: {
    welcome: (name) => (name ? `Welcome back, ${name}!` : "Welcome back!"),
    subtitle: "You're almost done — continue your booking right where you left off.",
    reviewHint: "Review your information below, adjust anything you need, and confirm your appointment.",
    locationLabel: "Your Little Bellies location",
    serviceLabel: "Selected service",
    invalidTitle: "This link is invalid or expired",
    invalidBody: "Please start a new booking or contact us for help.",
    loading: "Loading your booking…",
  },
  es: {
    welcome: (name) => (name ? `¡Hola de nuevo, ${name}!` : "¡Hola de nuevo!"),
    subtitle: "Ya casi terminas — continúa tu reserva justo donde la dejaste.",
    reviewHint: "Revisa tu información abajo, ajusta lo que necesites y confirma tu cita.",
    locationLabel: "Tu ubicación de Little Bellies",
    serviceLabel: "Servicio seleccionado",
    invalidTitle: "Este enlace no es válido o expiró",
    invalidBody: "Por favor inicia una nueva reserva o contáctanos para ayudarte.",
    loading: "Cargando tu reserva…",
  },
};

const BRAND = "#c2608e";

function formatPhone(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.length !== 10) return phone || "";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Public page (`/resume/:token`) that lets a user with an incomplete booking
 * continue where they left off. Shows a contextual header (greeting in the
 * lead's language + the location they were booking at) above the embedded
 * booking widget, which pre-fills the form from the same token.
 */
function ResumeBooking() {
  const { token } = useParams();
  const [iframeLoading, setIframeLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [site, setSite] = useState(null);
  // True once the lead fetch has settled (loaded, failed, or timed out). We hold
  // the widget iframe until then so it loads in the client's language on the
  // first render instead of flashing English and reloading.
  const [leadReady, setLeadReady] = useState(false);

  const parsed = useMemo(() => (token ? decodeResumeToken(token) : null), [token]);

  // Language from the lead ("Spanish" -> es), defaults to English.
  const lang =
    String(lead?.additionalInformation?.language || "").toLowerCase() === "spanish"
      ? "es"
      : "en";
  const t = COPY[lang];

  // Widget iframe URL, carrying the lead's language (`?lang`) so the embedded
  // booking form renders in es/en. Held until the lead fetch settles.
  const src = useMemo(
    () =>
      WIDGET_URL && parsed && leadReady
        ? buildWidgetResumeUrl(WIDGET_URL, token, lang)
        : "",
    [token, lang, leadReady, parsed]
  );

  // Fetch the lead (for name/language/service) and the site config (for the
  // location card). Both best-effort: the page still works with the plain
  // header if either fails.
  useEffect(() => {
    if (!parsed || !API_URL) {
      setLeadReady(true);
      return;
    }
    let cancelled = false;
    // Don't block the widget forever on a slow/failed lead fetch.
    const readyTimer = setTimeout(() => {
      if (!cancelled) setLeadReady(true);
    }, 2500);

    fetch(
      `${API_URL}/api/incomplete/booking/by-keys?date=${encodeURIComponent(
        parsed.timestampSite
      )}&id=${encodeURIComponent(parsed.leadId)}`
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && d.Item) setLead(d.Item);
        setLeadReady(true);
      })
      .catch(() => {
        if (!cancelled) setLeadReady(true);
      });

    fetch(`${API_URL}/api/config/sites`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || !Array.isArray(d.sites)) return;
        const bySite = d.sites.filter(
          (s) => String(s.site || "").split("-")[0] === parsed.siteId
        );
        setSite(bySite.length ? bySite : null);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      clearTimeout(readyTimer);
    };
  }, [parsed]);

  // Pick the exact location when the lead carries locationId; otherwise fall
  // back to location "-1" (the site's main location — same default the widget
  // uses), and only then to the first entry.
  const location = useMemo(() => {
    if (!site) return null;
    // locationId may live top-level (set by the step-1 PUT) or inside
    // additionalInformation (set by the create) depending on the lead's path.
    const locId = lead?.locationId ?? lead?.additionalInformation?.locationId;
    if (locId) {
      const exact = site.find((s) => s.site === `${lead.siteId || ""}-${locId}`);
      if (exact) return exact;
    }
    return site.find((s) => String(s.site || "").endsWith("-1")) || site[0];
  }, [site, lead]);

  const firstName = String(lead?.name || "").trim().split(/\s+/)[0] || "";

  if (!WIDGET_URL || !parsed) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "24px",
        }}
      >
        <div>
          <h2 style={{ color: BRAND }}>{COPY.en.invalidTitle}</h2>
          <p style={{ color: "#6c757d" }}>{COPY.en.invalidBody}</p>
          <p style={{ color: "#6c757d" }}>
            {COPY.es.invalidTitle}. {COPY.es.invalidBody}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#faf7fb" }}>
      {/* Contextual header */}
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          padding: "28px 16px 8px",
          textAlign: "center",
        }}
      >
        <h1 style={{ color: BRAND, fontSize: 26, marginBottom: 6 }}>
          {t.welcome(firstName)}
        </h1>
        <p style={{ color: "#2b2530", fontSize: 16, marginBottom: 4 }}>{t.subtitle}</p>
        <p style={{ color: "#8a8294", fontSize: 14, marginBottom: 16 }}>{t.reviewHint}</p>

        {location && (
          <div
            style={{
              display: "inline-block",
              textAlign: "left",
              background: "#ffffff",
              border: "1px solid #ece7ee",
              borderRadius: 14,
              boxShadow: "0 2px 10px rgba(155, 74, 112, 0.06)",
              padding: "14px 20px",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "#8a8294",
                marginBottom: 4,
              }}
            >
              {t.locationLabel}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#2b2530" }}>
              {location.name || `Little Bellies - ${location.storeName || ""}`}
            </div>
            {location.address && (
              <div style={{ fontSize: 13, color: "#6c757d", marginTop: 2 }}>
                {location.address}
              </div>
            )}
            {location.phone && (
              <div style={{ fontSize: 13, marginTop: 2 }}>
                <a href={`tel:1${String(location.phone).replace(/\D/g, "")}`} style={{ color: BRAND }}>
                  {formatPhone(location.phone)}
                </a>
              </div>
            )}
            {lead?.service ? (
              <div style={{ fontSize: 13, color: "#6c757d", marginTop: 6 }}>
                <span style={{ fontWeight: 600, color: "#2b2530" }}>{t.serviceLabel}: </span>
                {lead.service}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Booking widget */}
      <div style={{ position: "relative", minHeight: "100vh" }}>
        {(!src || iframeLoading) && (
          <div
            aria-live="polite"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: BRAND,
            }}
          >
            {t.loading}
          </div>
        )}
        {src && (
          <iframe
            title="Continue your booking"
            src={src}
            onLoad={() => setIframeLoading(false)}
            style={{ width: "100%", height: "100vh", border: 0 }}
            allow="clipboard-write"
          />
        )}
      </div>
    </div>
  );
}

export default ResumeBooking;
